import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, X, Check } from 'lucide-react';
import { getMyCongrats, markCongratsRead } from '../../services/congratsService';

function timeAgo(iso) {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return days === 1 ? 'yesterday' : `${days}d ago`;
}

function Sender({ message, photos }) {
  const photo = message.fromId ? photos?.[message.fromId] : null;
  if (photo) {
    return <img src={photo} alt="" className="w-7 h-7 rounded-full object-cover object-top flex-none mt-0.5" />;
  }
  return (
    <span className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold flex-none mt-0.5
                     bg-accent-soft text-accent-ink">
      {(message.fromName || '?').split(/\s+/).map((p) => p[0]).join('').toUpperCase().slice(0, 2)}
    </span>
  );
}

/**
 * Celebration inbox on the directory home.
 *
 * Reading used to destroy: only unread messages rendered, and the single X called
 * markCongratsRead on *everything*, so one click made every congratulations
 * permanently unreachable — a colleague's anniversary note was shown once and
 * then gone. Now each message dismisses individually, read messages stay
 * retrievable behind a one-line "earlier" toggle, and nothing is ever deleted.
 */
export function CongratsInbox({ currentUserId, photos }) {
  const [messages, setMessages] = useState([]);
  const [showRead, setShowRead] = useState(false);

  const refresh = useCallback(() => {
    if (!currentUserId) return;
    setMessages(getMyCongrats(currentUserId));
  }, [currentUserId]);

  useEffect(() => { refresh(); }, [refresh]);

  const unread = messages.filter((m) => !m.read);
  const read = messages.filter((m) => m.read);

  if (!currentUserId || messages.length === 0) return null;

  const dismissOne = (id) => {
    markCongratsRead(currentUserId, id);
    refresh();
  };

  const dismissAll = () => {
    markCongratsRead(currentUserId);
    refresh();
  };

  const shown = showRead ? [...unread, ...read] : unread;

  // All read and history collapsed: a single quiet line, so past notes stay
  // reachable without occupying the home view.
  if (unread.length === 0 && !showRead) {
    return (
      <button
        onClick={() => setShowRead(true)}
        className="flex items-center gap-2 h-8 px-3 mb-3 rounded-full bg-surface border border-line
                   text-[11px] text-ink-3 hover:text-ink-2 transition-colors"
      >
        <Sparkles size={12} className="text-amber-500" />
        <b className="font-mono font-semibold text-ink-2">{read.length}</b>
        {read.length === 1 ? 'congrats received' : 'congrats received'}
        <span className="text-accent font-semibold">View</span>
      </button>
    );
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -6 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -6 }}
        className="relative rounded-card px-3.5 py-3 mb-3 bg-surface border border-accent-line shadow-e1"
      >
        <div className="flex items-center gap-2 mb-2.5">
          <Sparkles size={13} className="text-amber-500" />
          <span className="text-[10px] font-bold tracking-[0.14em] uppercase text-accent-ink">
            {unread.length > 0
              ? `${unread.length} new ${unread.length === 1 ? 'congrats' : 'congrats'}`
              : 'Congrats received'}
          </span>

          {read.length > 0 && (
            <button
              onClick={() => setShowRead((v) => !v)}
              className="text-[10.5px] font-semibold text-ink-3 hover:text-ink-2 transition-colors"
            >
              {showRead ? 'Hide earlier' : `Show ${read.length} earlier`}
            </button>
          )}

          {unread.length > 0 && (
            <button
              onClick={dismissAll}
              title="Mark all as read"
              aria-label="Mark all as read"
              className="ml-auto text-[10.5px] font-semibold text-ink-3 hover:text-ink-2 transition-colors"
            >
              Mark all read
            </button>
          )}
        </div>

        <div className="space-y-1.5">
          {shown.map((m) => (
            <div
              key={m.id}
              className={`flex items-start gap-2.5 rounded-ctl px-3 py-2 border transition-colors
                ${m.read ? 'bg-surface-2 border-line opacity-75' : 'bg-surface border-line'}`}
            >
              <Sender message={m} photos={photos} />
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline gap-2">
                  <span className="text-[12px] font-semibold truncate text-ink">
                    {m.fromName || 'A colleague'}
                  </span>
                  <span className="text-[10px] flex-none text-ink-4">{timeAgo(m.sentAt)}</span>
                  {m.read && <span className="text-[9.5px] flex-none text-ink-4 uppercase tracking-[0.08em]">Read</span>}
                </div>
                <p className="text-[12px] mt-0.5 leading-relaxed text-ink-2">{m.message}</p>
              </div>

              {!m.read && (
                <button
                  onClick={() => dismissOne(m.id)}
                  title="Mark as read"
                  aria-label={`Mark message from ${m.fromName || 'a colleague'} as read`}
                  className="flex-none p-1 rounded-full text-ink-4 hover:text-accent transition-colors"
                >
                  <Check size={12} />
                </button>
              )}
            </div>
          ))}
        </div>

        {showRead && unread.length === 0 && (
          <button
            onClick={() => setShowRead(false)}
            aria-label="Collapse congrats"
            className="absolute top-2 right-2 p-1 rounded-full text-ink-4 hover:text-ink-2 transition-colors"
          >
            <X size={12} />
          </button>
        )}
      </motion.div>
    </AnimatePresence>
  );
}
